<template lang="pug">
    section
        el-row.no-padding(:gutter="40")
            el-col(:lg="12" :md="24")
                el-row.no-padding(:gutter="20")
                    el-col(:lg="6" :md="6")
                        el-form-item(:label="$t('Gender')" prop="gender")
                            el-select.is-full-width(placeholder="" v-model="gender")
                                el-option(:label="$t('Mr.')" value="M")
                                el-option(:label="$t('Mrs.')" value="F")
                                el-option(:label="$t('Undefined')" value="X")
                    el-col(:lg="9" :md="9")
                        el-form-item(:label="$t('First name')" prop="first_name")
                            el-input(v-model="firstName" maxlength="255" show-word-limit)
                    el-col(:lg="9" :md="9")
                        el-form-item(:label="$t('Last name')" prop="last_name")
                            el-input(v-model="lastName" maxlength="255" show-word-limit)
                el-row.no-padding(:gutter="20")
                    el-col(:lg="12" :md="12")
                        el-form-item(:label="$t('Email')" prop="email")
                            el-input(v-model="email" maxlength="255" show-word-limit)
                    el-col(:lg="12" :md="12")
                        el-form-item(:label="$t('Phone')" prop="phone")
                            el-input(v-model="phone" maxlength="255" show-word-limit)
                el-form-item(:label="$t('Company')" prop="company")
                    el-input(v-model="company" maxlength="255" show-word-limit)
                .password-reset-flex-col
                    el-form-item.switch(v-if="isUpdate && (isSuperUser || isOwnAccount)" size="mini" label="‎ ‎ ‎ ‎ ‎")
                        el-switch(
                            :active-text="$t('Change password')"
                            inactive-color="#ff4949"
                            v-model="changePassword"
                        )
                    el-form-item.input(v-if="! isUpdate || changePassword" :label="$t('Password')" prop="password")
                        el-input(v-model="password" show-password ref="password" maxlength="255" show-word-limit)
                        PasswordStrengthMeter(:password="password")
                //- Personal tokens for tooling (the Claude ticket skill): the own
                //- super-user account only, same component as the member's tokens.
                ApiTokens(
                    v-if="isUpdate && isOwnAccount && isSuperUser"
                    :description="$t('A token lets a tool read the CityOffer API as you for 90 days. Tools such as the Claude ticket skill request their own through the authorize page. A token is shown only once when created.')"
                    :fetch="userApiToken.fetchTokens"
                    :create="userApiToken.createToken"
                    :remove="userApiToken.deleteToken"
                )
            el-col(:lg="12" :md="24")
                h4.section-title {{ $t('Address') }}
                AddressCardInput(
                    v-if="hasAddress"
                    :form="addressForm"
                    formPath="address"
                    propPath="addresses.0"
                    :fields="['gender', 'first_name', 'last_name', 'email', 'phone', 'company', 'tax_id', 'street', 'zipcode', 'city', 'country']"
                )

        //- Business Card Panels — one per BC instance. Per-stock CMSes show
        //- a panel per branch; single-card CMSes show a single panel for the
        //- CMS-level card (existing behaviour).
        .business-cards-section(v-if="businessCardPanels.length > 0")
            h3.section-header {{ $t('Your Business Cards') }}
            .business-cards-grid
                BusinessCardPanel(
                    v-for="panel in businessCardPanels"
                    :key="panel.key"
                    :cms="panel.cms"
                    :business-card="panel.businessCard"
                )

        .flex-row.mt-6(v-if="isSuperUser")
                el-form-item.no-margin(size="mini" label=" ")
                    el-switch(
                        :active-text="emailVerifiedLabel"
                        v-model="emailVerified"
                    )
                el-form-item.no-margin(v-if="!emailVerified && !isUpdate" size="mini" label=" ")
                    el-switch(
                        :active-text="$t('Send email verification')"
                        v-model="sendEmailVerification"
                    )
</template>

<i18n>
{
    "en": {
        "Email verified at {date}": "Email verified at {date}"
    },
    "de": {
        "A token lets a tool read the CityOffer API as you for 90 days. Tools such as the Claude ticket skill request their own through the authorize page. A token is shown only once when created.": "Mit einem Token liest ein Werkzeug 90 Tage lang in deinem Namen aus der CityOffer API. Werkzeuge wie der Claude Ticket-Skill holen sich ihren über die Autorisierungsseite. Ein Token wird nur einmalig bei der Erstellung angezeigt.",
        "yyyy-MM-dd": "dd.MM.yyyy",
        "Gender": "Geschlecht",
        "Salutation": "Anrede",
        "First name": "Vorname",
        "Last name": "Nachname",
        "Email": "E-Mail",
        "Change password": "Passwort ändern",
        "Password": "Passwort",
        "Phone": "Telefon",
        "Company": "Firma",
        "Mr.": "Herr",
        "Mrs.": "Frau",
        "Undefined": "Unbekannt",
        "Address": "Adresse",
        "Your Business Cards": "Ihre Visitenkarten",
        "Mark email as verified": "E-Mail als verifiziert markieren",
        "Email verified at {date}": "E-Mail verifiziert am {date}",
        "Send email verification": "E-Mail-Verifizierung senden"
    }
}
</i18n>

<script>
import _ from 'lodash';

import { useAuthStore, useAccountStore, useSetupStore } from '@/stores';
import { timestamp2date } from '@/filters';
import AddressCardInput from '@/components/AddressCardInput';
import BusinessCardPanel from '@/components/BusinessCardPanel';
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter';
import ApiTokens from '@/components/ApiTokens';
import userApiToken from '@/store/api/system/userApiToken';

export default {
    name: 'Main',
    components: {
        AddressCardInput,
        BusinessCardPanel,
        PasswordStrengthMeter,
        ApiTokens
    },
    data() {
        return { userApiToken };
    },
    computed: {
        accountStore() {
            return useAccountStore()
        },
        form() {
            return this.accountStore.form
        },
        gender: {
            get() {
                return _.get(this.form, 'gender', '');
            },
            set(value) {
                this.accountStore.setFormGender(value)
            }
        },
        firstName: {
            get() {
                return _.get(this.form, 'first_name', '');
            },
            set(value) {
                this.accountStore.setFormFirstName(value)
            }
        },
        lastName: {
            get() {
                return _.get(this.form, 'last_name', '');
            },
            set(value) {
                this.accountStore.setFormLastName(value)
            }
        },
        email: {
            get() {
                return _.get(this.form, 'email', '');
            },
            set(value) {
                this.accountStore.setFormEmail(value)
            }
        },
        changePassword: {
            get() {
                return _.get(this.form, 'changePassword', false);
            },
            set(value) {
                this.accountStore.setFormChangePassword(value, !this.isSuperUser);
                if (value) {
                    this.$nextTick(() => {
                        const el = this.$refs.password.$el;
                        const input = el.getElementsByTagName('input');
                        if (input.length) input[0].select();
                    });
                }
            }
        },
        password: {
            get() {
                return _.get(this.form, 'password', '');
            },
            set(value) {
                this.accountStore.setFormPassword(value)
            }
        },
        phone: {
            get() {
                return _.get(this.form, 'phone', '');
            },
            set(value) {
                this.accountStore.setFormPhone(value)
            }
        },
        company: {
            get() {
                return _.get(this.form, 'company', '');
            },
            set(value) {
                this.accountStore.setFormCompany(value)
            }
        },
        isUpdate() {
            return !!_.get(this.form, 'id');
        },
        user() {
            return useAuthStore().user;
        },
        isSuperUser() {
            return useAuthStore().isSuperUser;
        },
        isOwnAccount() {
            return _.get(this.form, 'id') === _.get(this.user, 'id');
        },
        hasAddress() {
            const addresses = _.get(this.form, 'addresses', []);
            return addresses.length > 0;
        },
        addressForm() {
            // Wrap the first address in an object for AddressCardInput
            const addresses = _.get(this.form, 'addresses', []);
            if (addresses.length > 0) {
                return { address: addresses[0] };
            }
            return { address: {} };
        },
        emailVerified: {
            get() {
                return _.get(this.form, 'emailVerified', true);
            },
            set(value) {
                this.accountStore.setFormEmailVerified(value);
            }
        },
        emailVerifiedLabel() {
            const verifiedAt = _.get(this.form, 'email_verified_at')
            if (this.isUpdate && verifiedAt) {
                const formatted = timestamp2date(verifiedAt, this.$t('yyyy-MM-dd') + ', HH:mm')
                return this.$t('Email verified at {date}', { date: formatted })
            }
            return this.$t('Mark email as verified')
        },
        sendEmailVerification: {
            get() {
                return _.get(this.form, 'sendEmailVerification', false);
            },
            set(value) {
                this.accountStore.setFormSendEmailVerification(value);
            }
        },
        businessCardCmss() {
            // Get business card CMSs from the account being viewed, not the logged-in user
            const administrators = _.get(this.form, 'administrators', []);
            const allCmss = administrators.map(admin => _.get(admin, 'cms')).filter(Boolean);
            return allCmss.filter(cms => cms.is_business_card);
        },
        // Flat list of {cms, businessCard} pairs to render — one entry per BC.
        // Per-stock CMSes contribute one entry per branch; single-card CMSes
        // contribute one entry with `businessCard: null` (so the panel falls
        // back to `cms.businessCard` and keeps its original behaviour).
        businessCardPanels() {
            const panels = [];
            this.businessCardCmss.forEach((cms) => {
                const mode = _.get(cms, 'effective_business_card_mode')
                    || (cms.has_business_card_per_stock ? 'per_stock' : 'single');
                if (mode === 'per_stock') {
                    const branches = _.get(cms, 'businessCards', []) || [];
                    branches.forEach((branch) => {
                        panels.push({
                            key: `cms-${cms.id}-bc-${branch.id}`,
                            cms,
                            businessCard: branch,
                        });
                    });
                } else {
                    panels.push({
                        key: `cms-${cms.id}`,
                        cms,
                        businessCard: null,
                    });
                }
            });
            return panels;
        }
    }
};
</script>

<style lang="sass" scoped>
.password-reset-flex-col
    display: flex
    .switch
        padding-right: 20px
    .input
        flex-grow: 1
        :deep(.password-strength-label)
            display: none

.section-title
    margin: 0 0 15px 0
    font-weight: 500
    color: #303133

.business-cards-section
    margin-top: 40px
    padding-top: 30px
    border-top: 1px solid #ebeef5

    .section-header
        margin: 0 0 20px 0
        font-size: 18px
        font-weight: 500
        color: #303133

    .business-cards-grid
        display: flex
        flex-wrap: wrap
        gap: 20px
</style>
